"use client";

import type { ModelBrand } from "@saas/shared/config/ai-models";
import { cn } from "@ui/lib";
import { useId } from "react";

/** 视频模型另外几家（/image-to-video 用）；图片模型下拉按 ModelBrand 分组，不放进去 */
export type VideoModelBrand = "grok" | "minimax" | "wan" | "kling";

const BRAND_LABELS: Record<ModelBrand | VideoModelBrand, string> = {
	imagesart: "ImagesArt",
	seedream: "ByteDance",
	"nano-banana": "Google",
	gpt: "OpenAI",
	qwen: "Qwen",
	grok: "Grok",
	minimax: "MiniMax",
	wan: "Wan",
	kling: "Kling",
};

/**
 * 模型下拉里的品牌图标。第三方的来自 @lobehub/icons-static-svg（MIT），v3.0 用站内 logo
 * （public/images/logo.svg）。直接内联，不另加依赖。
 * 视频的 Grok / MiniMax / Wan / Kling 是 10/20 从 supermaker.ai/video 模型下拉扒的（老公要求）；
 * Seedance 用字节的（seedream），Veo / Gemini 用 Google 的（nano-banana）。
 */
export function ModelBrandIcon({
	brand,
	className,
}: {
	brand: ModelBrand | VideoModelBrand;
	className?: string;
}) {
	// Qwen / Kling / MiniMax / Wan 的渐变要 id，同一页会出现好几个图标（卡片 + 下拉），不能写死
	const gradientId = useId();
	const label = BRAND_LABELS[brand];
	const svgProps = {
		viewBox: "0 0 24 24",
		xmlns: "http://www.w3.org/2000/svg",
		className: cn("size-4 flex-none", className),
	} as const;

	switch (brand) {
		case "imagesart":
			return (
				<svg
					role="img"
					aria-label={label}
					{...svgProps}
					viewBox="6 0 452 402"
					fill="#1f11ed"
				>
					<g transform="translate(0,345) scale(0.1,-0.1)">
						<path d="M522 3428 c-26 -26 -13 -64 46 -134 321 -383 449 -623 487 -910 18 -136 19 -201 5 -332 -29 -268 -114 -524 -267 -807 -90 -164 -149 -255 -513 -787 -69 -100 -126 -186 -128 -190 -9 -21 221 89 903 432 764 384 877 444 1045 556 209 139 326 251 352 336 17 57 -9 230 -57 373 -89 268 -235 494 -466 724 -311 309 -697 537 -1203 707 -133 45 -183 53 -204 32z" />
					</g>
					<g transform="rotate(-10.0 248 231) translate(248 231) skewX(-8.0) scale(1 1.2) translate(-248 -231)">
						<g transform="translate(0,345) scale(0.1,-0.1)">
							<path d="M3060 2770 c-18 -18 -20 -33 -20 -187 0 -303 -30 -498 -109 -707 -77 -206 -179 -357 -364 -540 -128 -127 -139 -149 -99 -189 l21 -21 143 32 c301 66 500 90 818 99 379 10 689 -28 963 -119 67 -22 127 -36 138 -33 11 4 26 17 35 30 12 19 13 28 3 46 -9 18 -1070 1181 -1298 1424 -36 39 -89 96 -117 128 -56 62 -81 70 -114 37z" />
						</g>
					</g>
					<g transform="rotate(-6.0 229 333)">
						<g transform="translate(0,380) scale(0.1,-0.1)">
							<path d="M3670 933 c-132 -4 -510 -32 -670 -49 -1092 -114 -2282 -413 -2870 -721 -47 -25 -93 -53 -104 -63 -24 -24 -15 -63 19 -78 33 -15 2205 -17 2500 -2 312 16 575 60 817 137 407 128 779 342 1116 641 76 68 85 89 52 122 -19 19 -32 20 -397 18 -208 -1 -416 -3 -463 -5z" />
						</g>
					</g>
				</svg>
			);
		case "seedream":
			// Seedream 没有单独的公开 logo，用字节的
			return (
				<svg role="img" aria-label={label} {...svgProps}>
					<g fill="none" fillRule="evenodd">
						<path
							d="M14.944 18.587l-1.704-.445V10.01l1.824-.462c1-.254 1.84-.461 1.88-.453.032 0 .056 2.235.056 4.972v4.973l-.176-.008c-.104 0-.952-.207-1.88-.446z"
							fill="#00C8D2"
							fillRule="nonzero"
						/>
						<path
							d="M7 16.542c0-2.736.024-4.98.064-4.98.032-.008.872.2 1.88.454l1.816.461-.016 4.05-.024 4.049-1.632.422c-.896.23-1.736.445-1.856.469L7 21.523v-4.98z"
							fill="#3C8CFF"
							fillRule="nonzero"
						/>
						<path
							d="M19.24 12.477c0-9.03.008-9.515.144-9.475.072.024.784.207 1.576.406.792.207 1.576.405 1.744.445l.296.08-.016 8.56-.024 8.568-1.624.414c-.888.23-1.728.437-1.856.47l-.24.055v-9.523z"
							fill="#78E6DC"
							fillRule="nonzero"
						/>
						<path
							d="M1 12.509c0-4.678.024-8.505.064-8.505.032 0 .872.207 1.872.454l1.824.461v7.582c0 4.16-.016 7.574-.032 7.574-.024 0-.872.215-1.88.47L1 21.013v-8.505z"
							fill="#325AB4"
						/>
					</g>
				</svg>
			);
		case "nano-banana":
			return (
				<svg role="img" aria-label={label} {...svgProps}>
					<path
						d="M23 12.245c0-.905-.075-1.565-.236-2.25h-10.54v4.083h6.186c-.124 1.014-.797 2.542-2.294 3.569l-.021.136 3.332 2.53.23.022C21.779 18.417 23 15.593 23 12.245z"
						fill="#4285F4"
					/>
					<path
						d="M12.225 23c3.03 0 5.574-.978 7.433-2.665l-3.542-2.688c-.948.648-2.22 1.1-3.891 1.1a6.745 6.745 0 01-6.386-4.572l-.132.011-3.465 2.628-.045.124C4.043 20.531 7.835 23 12.225 23z"
						fill="#34A853"
					/>
					<path
						d="M5.84 14.175A6.65 6.65 0 015.463 12c0-.758.138-1.491.361-2.175l-.006-.147-3.508-2.67-.115.054A10.831 10.831 0 001 12c0 1.772.436 3.447 1.197 4.938l3.642-2.763z"
						fill="#FBBC05"
					/>
					<path
						d="M12.225 5.253c2.108 0 3.529.892 4.34 1.638l3.167-3.031C17.787 2.088 15.255 1 12.225 1 7.834 1 4.043 3.469 2.197 7.062l3.63 2.763a6.77 6.77 0 016.398-4.572z"
						fill="#EB4335"
					/>
				</svg>
			);
		case "gpt":
			return (
				<svg
					role="img"
					aria-label={label}
					{...svgProps}
					fill="currentColor"
					fillRule="evenodd"
				>
					<path d="M21.55 10.004a5.416 5.416 0 00-.478-4.501c-1.217-2.09-3.662-3.166-6.05-2.66A5.59 5.59 0 0010.831 1C8.39.995 6.224 2.546 5.473 4.838A5.553 5.553 0 001.76 7.496a5.487 5.487 0 00.691 6.5 5.416 5.416 0 00.477 4.502c1.217 2.09 3.662 3.165 6.05 2.66A5.586 5.586 0 0013.168 23c2.443.006 4.61-1.546 5.361-3.84a5.553 5.553 0 003.715-2.66 5.488 5.488 0 00-.693-6.497v.001zm-8.381 11.558a4.199 4.199 0 01-2.675-.954c.034-.018.093-.05.132-.074l4.44-2.53a.71.71 0 00.364-.623v-6.176l1.877 1.069c.02.01.033.029.036.05v5.115c-.003 2.274-1.87 4.118-4.174 4.123zM4.192 17.78a4.059 4.059 0 01-.498-2.763c.032.02.09.055.131.078l4.44 2.53c.225.13.504.13.73 0l5.42-3.088v2.138a.068.068 0 01-.027.057L9.9 19.288c-1.999 1.136-4.552.46-5.707-1.51h-.001zM3.023 8.216A4.15 4.15 0 015.198 6.41l-.002.151v5.06a.711.711 0 00.364.624l5.42 3.087-1.876 1.07a.067.067 0 01-.063.005l-4.489-2.559c-1.995-1.14-2.679-3.658-1.53-5.63h.001zm15.417 3.54l-5.42-3.088L14.896 7.6a.067.067 0 01.063-.006l4.489 2.557c1.998 1.14 2.683 3.662 1.529 5.633a4.163 4.163 0 01-2.174 1.807V12.38a.71.71 0 00-.363-.623zm1.867-2.773a6.04 6.04 0 00-.132-.078l-4.44-2.53a.731.731 0 00-.729 0l-5.42 3.088V7.325a.068.068 0 01.027-.057L14.1 4.713c2-1.137 4.555-.46 5.707 1.513.487.833.664 1.809.499 2.757h.001zm-11.741 3.81l-1.877-1.068a.065.065 0 01-.036-.051V6.559c.001-2.277 1.873-4.122 4.181-4.12.976 0 1.92.338 2.671.954-.034.018-.092.05-.131.073l-4.44 2.53a.71.71 0 00-.365.623l-.003 6.173v.002zm1.02-2.168L12 9.25l2.414 1.375v2.75L12 14.75l-2.415-1.375v-2.75z" />
				</svg>
			);
		case "qwen":
			return (
				<svg role="img" aria-label={label} {...svgProps}>
					<defs>
						<linearGradient
							id={gradientId}
							x1="0%"
							x2="100%"
							y1="0%"
							y2="0%"
						>
							<stop
								offset="0%"
								stopColor="#00055F"
								stopOpacity=".84"
							/>
							<stop
								offset="100%"
								stopColor="#6F69F7"
								stopOpacity=".84"
							/>
						</linearGradient>
					</defs>
					<path
						d="M12.604 1.34c.393.69.784 1.382 1.174 2.075a.18.18 0 00.157.091h5.552c.174 0 .322.11.446.327l1.454 2.57c.19.337.24.478.024.837-.26.43-.513.864-.76 1.3l-.367.658c-.106.196-.223.28-.04.512l2.652 4.637c.172.301.111.494-.043.77-.437.785-.882 1.564-1.335 2.34-.159.272-.352.375-.68.37-.777-.016-1.552-.01-2.327.016a.099.099 0 00-.081.05 575.097 575.097 0 01-2.705 4.74c-.169.293-.38.363-.725.364-.997.003-2.002.004-3.017.002a.537.537 0 01-.465-.271l-1.335-2.323a.09.09 0 00-.083-.049H4.982c-.285.03-.553-.001-.805-.092l-1.603-2.77a.543.543 0 01-.002-.54l1.207-2.12a.198.198 0 000-.197 550.951 550.951 0 01-1.875-3.272l-.79-1.395c-.16-.31-.173-.496.095-.965.465-.813.927-1.625 1.387-2.436.132-.234.304-.334.584-.335a338.3 338.3 0 012.589-.001.124.124 0 00.107-.063l2.806-4.895a.488.488 0 01.422-.246c.524-.001 1.053 0 1.583-.006L11.704 1c.341-.003.724.032.9.34zm-3.432.403a.06.06 0 00-.052.03L6.254 6.788a.157.157 0 01-.135.078H3.253c-.056 0-.07.025-.041.074l5.81 10.156c.025.042.013.062-.034.063l-2.795.015a.218.218 0 00-.2.116l-1.32 2.31c-.044.078-.021.118.068.118l5.716.008c.046 0 .08.02.104.061l1.403 2.454c.046.081.092.082.139 0l5.006-8.76.783-1.382a.055.055 0 01.096 0l1.424 2.53a.122.122 0 00.107.062l2.763-.02a.04.04 0 00.035-.02.041.041 0 000-.04l-2.9-5.086a.108.108 0 010-.113l.293-.507 1.12-1.977c.024-.041.012-.062-.035-.062H9.2c-.059 0-.073-.026-.043-.077l1.434-2.505a.107.107 0 000-.114L9.225 1.774a.06.06 0 00-.053-.031zm6.29 8.02c.046 0 .058.02.034.06l-.832 1.465-2.613 4.585a.056.056 0 01-.05.029.058.058 0 01-.05-.029L8.498 9.841c-.02-.034-.01-.052.028-.054l.216-.012 6.722-.012z"
						fill={`url(#${gradientId})`}
						fillRule="nonzero"
					/>
				</svg>
			);
		case "grok":
			// 原图是给黑底的白色，改成黑
			return (
				<svg
					role="img"
					aria-label={label}
					{...svgProps}
					viewBox="0 0 1024 1024"
				>
					<g>
						<path
							opacity={0.983}
							fill="#0a0a08"
							d="M 1023.5,20.5 C 1023.5,21.1667 1023.5,21.8333 1023.5,22.5C 991.047,65.3761 960.547,109.709 932,155.5C 908.461,194.45 893.461,236.45 887,281.5C 883.556,311.535 883.89,341.535 888,371.5C 892.562,400.311 897.896,428.978 904,457.5C 922.229,586.802 886.395,698.302 796.5,792C 703.251,880.634 592.584,915.968 464.5,898C 405.471,887.639 351.471,865.472 302.5,831.5C 341.015,812.826 379.848,794.826 419,777.5C 492.433,805.984 565.6,805.484 638.5,776C 736.29,728.653 791.457,650.82 804,542.5C 807.3,497.93 800.633,454.93 784,413.5C 775.753,396.638 762.586,390.804 744.5,396C 741.833,397.333 739.167,398.667 736.5,400C 622.948,484.552 509.115,568.719 395,652.5C 394.833,652.167 394.667,651.833 394.5,651.5C 506.819,538.014 619.319,424.68 732,311.5C 829.035,214.299 926.201,117.299 1023.5,20.5 Z"
						/>
					</g>{" "}
					<g>
						<path
							opacity={0.985}
							fill="#0a0a08"
							d="M -0.5,1004.5 C -0.5,1003.83 -0.5,1003.17 -0.5,1002.5C 7.75587,991.732 16.2559,981.066 25,970.5C 49.9669,943.198 75.3003,916.198 101,889.5C 124.409,864.766 144.742,837.766 162,808.5C 183.734,769.963 189.067,729.296 178,686.5C 170.095,665.358 162.762,644.025 156,622.5C 125.134,497.345 148.134,383.345 225,280.5C 302.719,186.146 403.053,137.813 526,135.5C 600.035,135.961 668.535,155.461 731.5,194C 737.779,197.946 743.779,202.279 749.5,207C 711.025,225.603 672.192,243.437 633,260.5C 546.981,228.012 463.814,234.179 383.5,279C 305.526,327.686 259.693,397.853 246,489.5C 236.617,577.033 263.117,651.533 325.5,713C 216.962,810.374 108.296,907.541 -0.5,1004.5 Z"
						/>
					</g>
				</svg>
			);
		case "minimax":
			// MiniMax（海螺）自带圆角底色
			return (
				<svg
					role="img"
					aria-label={label}
					{...svgProps}
					viewBox="0 0 64 64"
				>
					<defs>
						{" "}
						<linearGradient
							id={`${gradientId}-minimax`}
							x1="5"
							y1="4"
							x2="59"
							y2="60"
							gradientUnits="userSpaceOnUse"
						>
							{" "}
							<stop stopColor="#E21680" />{" "}
							<stop offset="1" stopColor="#FF633A" />{" "}
						</linearGradient>{" "}
					</defs>{" "}
					<rect
						width="64"
						height="64"
						rx="14"
						fill={`url(#${gradientId}-minimax)`}
					/>{" "}
					<path
						d="M26.6782 7.96523C26.6782 7.02436 25.913 6.26087 24.9739 6.26087C24.0348 6.26087 23.2695 7.0261 23.2695 7.96523V36.2139C23.2695 38.4 21.4904 40.1791 19.3043 40.1791C17.1183 40.1791 15.3391 38.4 15.3391 36.2139V18.0904C15.3391 17.1496 14.5739 16.3861 13.6348 16.3861C12.6956 16.3861 11.9304 17.1513 11.9304 18.0904V25.7722C11.9304 27.9583 10.1513 29.7374 7.96518 29.7374C5.7791 29.7374 4 27.9583 4 25.7722V22.9878C4 22.3635 4.50609 21.8574 5.13043 21.8574C5.75478 21.8574 6.26087 22.3635 6.26087 22.9878V25.7722C6.26087 26.713 7.02605 27.4765 7.96518 27.4765C8.90431 27.4765 9.66954 26.7113 9.66954 25.7722V18.0904C9.66954 15.9044 11.4487 14.1252 13.6348 14.1252C15.8209 14.1252 17.6 15.9044 17.6 18.0904V36.2139C17.6 37.1548 18.3652 37.9183 19.3043 37.9183C20.2435 37.9183 21.0087 37.153 21.0087 36.2139V25.1322V7.96523C21.0087 5.77914 22.7878 4 24.9739 4C27.16 4 28.9391 5.77914 28.9391 7.96523V31.3565C28.9391 31.9809 28.433 32.487 27.8087 32.487C27.1843 32.487 26.6782 31.9809 26.6782 31.3565V7.96523ZM47.6539 14.1252C45.4678 14.1252 43.6887 15.9044 43.6887 18.0904V33.2296C43.6887 34.1704 42.9235 34.9339 41.9843 34.9339C41.0452 34.9339 40.28 34.1687 40.28 33.2296V7.96523C40.28 5.77914 38.5008 4 36.3148 4C34.1287 4 32.3496 5.77914 32.3496 7.96523V40.0348C32.3496 40.9756 31.5843 41.7391 30.6452 41.7391C29.7061 41.7391 28.9409 40.9739 28.9409 40.0348V36.0643C28.9409 35.44 28.4348 34.9339 27.8104 34.9339C27.1861 34.9339 26.68 35.44 26.68 36.0643V40.0348C26.68 42.2209 28.4591 44 30.6452 44C32.8313 44 34.6104 42.2209 34.6104 40.0348V7.96523C34.6104 7.02436 35.3756 6.26087 36.3148 6.26087C37.2539 6.26087 38.0191 7.0261 38.0191 7.96523V33.2296C38.0191 35.4156 39.7982 37.1948 41.9843 37.1948C44.1704 37.1948 45.9496 35.4156 45.9496 33.2296V18.0904C45.9496 17.1496 46.7148 16.3861 47.6539 16.3861C48.593 16.3861 49.3582 17.1513 49.3582 18.0904V31.3565C49.3582 31.9809 49.8643 32.487 50.4887 32.487C51.113 32.487 51.6191 31.9809 51.6191 31.3565V18.0904C51.6191 15.9044 49.84 14.1252 47.6539 14.1252Z"
						fill="white"
						transform="translate(8.64 10.88) scale(0.84)"
					/>
				</svg>
			);
		case "wan":
			// 原图三条主形是白色（黑底用），改成 Wan 的品牌紫，渐变阴影保留
			return (
				<svg
					role="img"
					aria-label={label}
					{...svgProps}
					viewBox="0 0 32 32"
				>
					<path
						d="M11.6443 0.753845L13.8229 4.52806L11.6443 8.29908H29.0703L26.8917 12.0717H9.90298L7.50568 7.922L11.6443 0.753845Z"
						fill="#615CED"
					/>{" "}
					<path
						d="M2.93243 8.29797H7.28815L15.7809 23.0114L13.3868 27.1627L5.11108 27.1611L7.28973 23.3869L11.6455 23.3885L2.93243 8.29797Z"
						fill="#615CED"
					/>{" "}
					<path
						d="M13.8218 27.1617L16.0004 30.9343L24.7134 15.8438L26.8905 19.6165H31.2478L27.1108 12.4483L22.3193 12.4499L13.8218 27.1617Z"
						fill="#615CED"
					/>{" "}
					<path
						d="M11.6443 0.753845L13.8229 4.52806L11.6443 8.29908H29.0703L26.8917 12.0717H9.90298L7.50568 7.922L11.6443 0.753845Z"
						fill={`url(#${gradientId}-wan-0)`}
					/>{" "}
					<path
						d="M5.1095 27.1627L7.28815 23.3885L11.6439 23.3901L2.93243 8.29797H7.28656L15.7809 23.0114L13.3868 27.1627H5.1095Z"
						fill={`url(#${gradientId}-wan-1)`}
					/>{" "}
					<path
						d="M31.2496 19.6165H26.8923L24.7136 15.8438L16.0022 30.9343L13.8235 27.1633L22.3195 12.4499L27.1109 12.4483L31.2496 19.6165Z"
						fill={`url(#${gradientId}-wan-2)`}
					/>{" "}
					<path
						d="M31.9002 19.2387L28.1989 12.8263L27.7631 12.0705L29.7231 8.67659C29.7897 8.56088 29.8245 8.42937 29.8245 8.29946C29.8245 8.16955 29.7912 8.03804 29.7231 7.92237L27.5445 4.14973C27.4779 4.03406 27.3813 3.939 27.2687 3.87403C27.1563 3.80907 27.0263 3.77263 26.8917 3.77263H18.6143L16.6544 0.377104C16.5197 0.144187 16.2709 0 16.0016 0H11.6459C11.5112 0 11.3813 0.0364428 11.2687 0.101406C11.1563 0.16637 11.0596 0.261438 10.9915 0.377104L7.28857 6.79107L6.85285 7.54369H2.93285C2.79817 7.54369 2.66825 7.58013 2.55575 7.64506C2.44325 7.71004 2.3466 7.80511 2.27847 7.92077L0.101406 11.6918C0.0348584 11.8075 0 11.939 0 12.0689C0 12.1989 0.0332739 12.3304 0.101406 12.446L4.24005 19.6142L2.28006 23.0097C2.21351 23.127 2.17865 23.2569 2.17865 23.3868C2.17865 23.5167 2.21192 23.6483 2.28006 23.7639L4.4587 27.5365C4.52525 27.6522 4.6219 27.7473 4.7344 27.8122C4.8469 27.8772 4.97683 27.9137 5.1115 27.9137H13.3872L15.3472 31.3092C15.4819 31.5421 15.7306 31.6862 16 31.6862H20.3557C20.4904 31.6862 20.6203 31.6498 20.7328 31.5849C20.8453 31.5199 20.942 31.4248 21.0085 31.3092L25.1471 24.141H29.0671C29.2018 24.141 29.3317 24.1046 29.4442 24.0396C29.5567 23.9747 29.6534 23.8796 29.7199 23.7639L31.8986 19.9913C31.9667 19.8756 32 19.7441 32 19.6142C32 19.4842 31.9667 19.3527 31.8986 19.2371L31.9002 19.2387ZM11.6443 0.752625L13.8229 4.52525L11.6443 8.29786H29.0703L26.8932 12.0689H9.90297L7.50565 7.91917L11.6443 0.752625ZM13.3872 27.1626H5.1115L7.29017 23.3868H11.6459L2.93286 8.29786H7.28857L9.46565 12.0705L15.7813 23.0128L13.3856 27.1642L13.3872 27.1626ZM26.8901 19.6142L24.713 15.8415L16 30.9321L13.8214 27.1594L16 23.3868L22.3173 12.4476H27.1103L31.2474 19.6126H26.8901V19.6142Z"
						fill={`url(#${gradientId}-wan-3)`}
					/>{" "}
					<path
						d="M31.9002 19.2387L28.1989 12.8263L27.7631 12.0705L29.7231 8.67659C29.7897 8.56088 29.8245 8.42937 29.8245 8.29946C29.8245 8.16955 29.7912 8.03804 29.7231 7.92237L27.5445 4.14973C27.4779 4.03406 27.3813 3.939 27.2687 3.87403C27.1563 3.80907 27.0263 3.77263 26.8917 3.77263H18.6143L16.6544 0.377104C16.5197 0.144187 16.2709 0 16.0016 0H11.6459C11.5112 0 11.3813 0.0364428 11.2687 0.101406C11.1563 0.16637 11.0596 0.261438 10.9915 0.377104L7.28857 6.79107L6.85285 7.54369H2.93285C2.79817 7.54369 2.66825 7.58013 2.55575 7.64506C2.44325 7.71004 2.3466 7.80511 2.27847 7.92077L0.101406 11.6918C0.0348584 11.8075 0 11.939 0 12.0689C0 12.1989 0.0332739 12.3304 0.101406 12.446L4.24005 19.6142L2.28006 23.0097C2.21351 23.127 2.17865 23.2569 2.17865 23.3868C2.17865 23.5167 2.21192 23.6483 2.28006 23.7639L4.4587 27.5365C4.52525 27.6522 4.6219 27.7473 4.7344 27.8122C4.8469 27.8772 4.97683 27.9137 5.1115 27.9137H13.3872L15.3472 31.3092C15.4819 31.5421 15.7306 31.6862 16 31.6862H20.3557C20.4904 31.6862 20.6203 31.6498 20.7328 31.5849C20.8453 31.5199 20.942 31.4248 21.0085 31.3092L25.1471 24.141H29.0671C29.2018 24.141 29.3317 24.1046 29.4442 24.0396C29.5567 23.9747 29.6534 23.8796 29.7199 23.7639L31.8986 19.9913C31.9667 19.8756 32 19.7441 32 19.6142C32 19.4842 31.9667 19.3527 31.8986 19.2371L31.9002 19.2387ZM11.6443 0.752625L13.8229 4.52525L11.6443 8.29786H29.0703L26.8932 12.0689H9.90297L7.50565 7.91917L11.6443 0.752625ZM13.3872 27.1626H5.1115L7.29017 23.3868H11.6459L2.93286 8.29786H7.28857L9.46565 12.0705L15.7813 23.0128L13.3856 27.1642L13.3872 27.1626ZM26.8901 19.6142L24.713 15.8415L16 30.9321L13.8214 27.1594L16 23.3868L22.3173 12.4476H27.1103L31.2474 19.6126H26.8901V19.6142Z"
						fill={`url(#${gradientId}-wan-4)`}
					/>{" "}
					<path
						d="M31.9002 19.2387L28.1989 12.8263L27.7631 12.0705L29.7231 8.67659C29.7897 8.56088 29.8245 8.42937 29.8245 8.29946C29.8245 8.16955 29.7912 8.03804 29.7231 7.92237L27.5445 4.14973C27.4779 4.03406 27.3813 3.939 27.2687 3.87403C27.1563 3.80907 27.0263 3.77263 26.8917 3.77263H18.6143L16.6544 0.377104C16.5197 0.144187 16.2709 0 16.0016 0H11.6459C11.5112 0 11.3813 0.0364428 11.2687 0.101406C11.1563 0.16637 11.0596 0.261438 10.9915 0.377104L7.28857 6.79107L6.85285 7.54369H2.93285C2.79817 7.54369 2.66825 7.58013 2.55575 7.64506C2.44325 7.71004 2.3466 7.80511 2.27847 7.92077L0.101406 11.6918C0.0348584 11.8075 0 11.939 0 12.0689C0 12.1989 0.0332739 12.3304 0.101406 12.446L4.24005 19.6142L2.28006 23.0097C2.21351 23.127 2.17865 23.2569 2.17865 23.3868C2.17865 23.5167 2.21192 23.6483 2.28006 23.7639L4.4587 27.5365C4.52525 27.6522 4.6219 27.7473 4.7344 27.8122C4.8469 27.8772 4.97683 27.9137 5.1115 27.9137H13.3872L15.3472 31.3092C15.4819 31.5421 15.7306 31.6862 16 31.6862H20.3557C20.4904 31.6862 20.6203 31.6498 20.7328 31.5849C20.8453 31.5199 20.942 31.4248 21.0085 31.3092L25.1471 24.141H29.0671C29.2018 24.141 29.3317 24.1046 29.4442 24.0396C29.5567 23.9747 29.6534 23.8796 29.7199 23.7639L31.8986 19.9913C31.9667 19.8756 32 19.7441 32 19.6142C32 19.4842 31.9667 19.3527 31.8986 19.2371L31.9002 19.2387ZM11.6443 0.752625L13.8229 4.52525L11.6443 8.29786H29.0703L26.8932 12.0689H9.90297L7.50565 7.91917L11.6443 0.752625ZM13.3872 27.1626H5.1115L7.29017 23.3868H11.6459L2.93286 8.29786H7.28857L9.46565 12.0705L15.7813 23.0128L13.3856 27.1642L13.3872 27.1626ZM26.8901 19.6142L24.713 15.8415L16 30.9321L13.8214 27.1594L16 23.3868L22.3173 12.4476H27.1103L31.2474 19.6126H26.8901V19.6142Z"
						fill={`url(#${gradientId}-wan-5)`}
					/>{" "}
					<defs>
						{" "}
						<linearGradient
							id={`${gradientId}-wan-0`}
							x1="9.79365"
							y1="11.8832"
							x2="16.3546"
							y2="-1.86734"
							gradientUnits="userSpaceOnUse"
						>
							{" "}
							<stop stopColor="#6964ED" stopOpacity="0.53" />{" "}
							<stop
								offset="0.01"
								stopColor="#8884F1"
								stopOpacity="0.62"
							/>{" "}
							<stop
								offset="0.03"
								stopColor="#A4A2F4"
								stopOpacity="0.72"
							/>{" "}
							<stop
								offset="0.04"
								stopColor="#BDBBF7"
								stopOpacity="0.79"
							/>{" "}
							<stop
								offset="0.06"
								stopColor="#D2D0F9"
								stopOpacity="0.86"
							/>{" "}
							<stop
								offset="0.07"
								stopColor="#E2E1FB"
								stopOpacity="0.91"
							/>{" "}
							<stop
								offset="0.09"
								stopColor="#EFEFFD"
								stopOpacity="0.95"
							/>{" "}
							<stop
								offset="0.12"
								stopColor="#F8F8FE"
								stopOpacity="0.98"
							/>{" "}
							<stop offset="0.15" stopColor="#FDFDFE" />{" "}
							<stop offset="0.25" stopColor="white" />{" "}
						</linearGradient>{" "}
						<linearGradient
							id={`${gradientId}-wan-1`}
							x1="15.6744"
							y1="23.2002"
							x2="-1.5739"
							y2="18.5812"
							gradientUnits="userSpaceOnUse"
						>
							{" "}
							<stop stopColor="#6964ED" stopOpacity="0.53" />{" "}
							<stop
								offset="0.01"
								stopColor="#8884F1"
								stopOpacity="0.62"
							/>{" "}
							<stop
								offset="0.03"
								stopColor="#A4A2F4"
								stopOpacity="0.72"
							/>{" "}
							<stop
								offset="0.04"
								stopColor="#BDBBF7"
								stopOpacity="0.79"
							/>{" "}
							<stop
								offset="0.06"
								stopColor="#D2D0F9"
								stopOpacity="0.86"
							/>{" "}
							<stop
								offset="0.07"
								stopColor="#E2E1FB"
								stopOpacity="0.91"
							/>{" "}
							<stop
								offset="0.09"
								stopColor="#EFEFFD"
								stopOpacity="0.95"
							/>{" "}
							<stop
								offset="0.12"
								stopColor="#F8F8FE"
								stopOpacity="0.98"
							/>{" "}
							<stop offset="0.15" stopColor="#FDFDFE" />{" "}
							<stop offset="0.25" stopColor="white" />{" "}
						</linearGradient>{" "}
						<linearGradient
							id={`${gradientId}-wan-2`}
							x1="22.5368"
							y1="12.4482"
							x2="22.5375"
							y2="30.9348"
							gradientUnits="userSpaceOnUse"
						>
							{" "}
							<stop stopColor="#6964ED" stopOpacity="0.53" />{" "}
							<stop
								offset="0.01"
								stopColor="#8884F1"
								stopOpacity="0.62"
							/>{" "}
							<stop
								offset="0.03"
								stopColor="#A4A2F4"
								stopOpacity="0.72"
							/>{" "}
							<stop
								offset="0.04"
								stopColor="#BDBBF7"
								stopOpacity="0.79"
							/>{" "}
							<stop
								offset="0.06"
								stopColor="#D2D0F9"
								stopOpacity="0.86"
							/>{" "}
							<stop
								offset="0.07"
								stopColor="#E2E1FB"
								stopOpacity="0.91"
							/>{" "}
							<stop
								offset="0.09"
								stopColor="#EFEFFD"
								stopOpacity="0.95"
							/>{" "}
							<stop
								offset="0.12"
								stopColor="#F8F8FE"
								stopOpacity="0.98"
							/>{" "}
							<stop offset="0.15" stopColor="#FDFDFE" />{" "}
							<stop offset="0.25" stopColor="white" />{" "}
						</linearGradient>{" "}
						<linearGradient
							id={`${gradientId}-wan-3`}
							x1="29.7231"
							y1="7.92077"
							x2="2.41794"
							y2="23.9996"
							gradientUnits="userSpaceOnUse"
						>
							{" "}
							<stop stopColor="#615CED" />{" "}
							<stop offset="0.17" stopColor="#6156EC" />{" "}
							<stop offset="0.38" stopColor="#6147E9" />{" "}
							<stop offset="0.61" stopColor="#622FE4" />{" "}
							<stop offset="0.62" stopColor="#632DE4" />{" "}
						</linearGradient>{" "}
						<linearGradient
							id={`${gradientId}-wan-4`}
							x1="0"
							y1="15.8431"
							x2="32.0016"
							y2="15.8431"
							gradientUnits="userSpaceOnUse"
						>
							{" "}
							<stop stopColor="#615CED" />{" "}
							<stop
								offset="0.04"
								stopColor="#615CED"
								stopOpacity="0.96"
							/>{" "}
							<stop
								offset="0.09"
								stopColor="#615CED"
								stopOpacity="0.86"
							/>{" "}
							<stop
								offset="0.17"
								stopColor="#615CED"
								stopOpacity="0.69"
							/>{" "}
							<stop
								offset="0.25"
								stopColor="#615CED"
								stopOpacity="0.46"
							/>{" "}
							<stop
								offset="0.35"
								stopColor="#615CED"
								stopOpacity="0.16"
							/>{" "}
							<stop
								offset="0.4"
								stopColor="#615CED"
								stopOpacity="0"
							/>{" "}
						</linearGradient>{" "}
						<linearGradient
							id={`${gradientId}-wan-5`}
							x1="24.0016"
							y1="1.98534"
							x2="8.23646"
							y2="29.8332"
							gradientUnits="userSpaceOnUse"
						>
							{" "}
							<stop stopColor="#A7A5F6" />{" "}
							<stop
								offset="0.05"
								stopColor="#9895F4"
								stopOpacity="0.79"
							/>{" "}
							<stop
								offset="0.12"
								stopColor="#8784F1"
								stopOpacity="0.55"
							/>{" "}
							<stop
								offset="0.19"
								stopColor="#7975F0"
								stopOpacity="0.35"
							/>{" "}
							<stop
								offset="0.25"
								stopColor="#6F6AEE"
								stopOpacity="0.2"
							/>{" "}
							<stop
								offset="0.31"
								stopColor="#6762ED"
								stopOpacity="0.09"
							/>{" "}
							<stop
								offset="0.36"
								stopColor="#625DED"
								stopOpacity="0.02"
							/>{" "}
							<stop
								offset="0.4"
								stopColor="#615CED"
								stopOpacity="0"
							/>{" "}
						</linearGradient>{" "}
					</defs>
				</svg>
			);
		case "kling":
			// supermaker 用的就是 lobe-icons 这版
			return (
				<svg role="img" aria-label={label} {...svgProps}>
					<path
						d="M5.412 13.775A23.193 23.193 0 017.41 9.32c3.17-5.492 7.795-8.757 10.33-7.294C12.038-1.266 4.598.944 1.122 6.964A13.378 13.378 0 00.085 9.22c-.259.739.092 1.534.77 1.926l4.557 2.63z"
						fill={`url(#${gradientId}-kling-0)`}
					/>
					<path
						d="M18.588 10.164a23.188 23.188 0 01-1.999 4.455c-3.17 5.492-7.795 8.758-10.33 7.294 5.703 3.293 13.143 1.082 16.619-4.938a13.392 13.392 0 001.037-2.255c.259-.738-.092-1.534-.77-1.925l-4.557-2.63z"
						fill={`url(#${gradientId}-kling-1)`}
					/>
					<path
						d="M16.59 14.62c3.17-5.492 3.686-11.13 1.15-12.594C15.207.563 10.582 3.83 7.41 9.32c2.074-3.59 5.809-5.315 8.344-3.852 2.534 1.464 2.908 5.56.835 9.151z"
						fill={`url(#${gradientId}-kling-2)`}
					/>
					<path
						d="M7.41 9.32c-3.17 5.492-3.686 11.13-1.15 12.593 2.534 1.464 7.159-1.802 10.33-7.294-2.074 3.591-5.809 5.316-8.344 3.852-2.534-1.463-2.908-5.56-.835-9.15z"
						fill={`url(#${gradientId}-kling-3)`}
					/>
					<defs>
						<radialGradient
							cx="0"
							cy="0"
							gradientTransform="matrix(7.47772 -12.51022 17.14368 10.24728 5.173 13.637)"
							gradientUnits="userSpaceOnUse"
							id={`${gradientId}-kling-0`}
							r="1"
						>
							<stop offset=".095" stopColor="#FFF959" />
							<stop offset=".326" stopColor="#0DF35E" />
							<stop offset=".64" stopColor="#0BF2F9" />
							<stop offset="1" stopColor="#04A6F0" />
						</radialGradient>
						<radialGradient
							cx="0"
							cy="0"
							gradientTransform="rotate(120.868 6.491 10.491) scale(14.5747 19.9728)"
							gradientUnits="userSpaceOnUse"
							id={`${gradientId}-kling-1`}
							r="1"
						>
							<stop offset=".095" stopColor="#FFF959" />
							<stop offset=".326" stopColor="#0DF35E" />
							<stop offset=".64" stopColor="#0BF2F9" />
							<stop offset="1" stopColor="#04A6F0" />
						</radialGradient>
						<linearGradient
							gradientUnits="userSpaceOnUse"
							id={`${gradientId}-kling-2`}
							x1="15.578"
							x2="18.062"
							y1="1.798"
							y2="9.861"
						>
							<stop stopColor="#003EFF" />
							<stop offset="1" stopColor="#0BFFE7" />
						</linearGradient>
						<linearGradient
							gradientUnits="userSpaceOnUse"
							id={`${gradientId}-kling-3`}
							x1="8.422"
							x2="5.938"
							y1="22.142"
							y2="14.079"
						>
							<stop stopColor="#003EFF" />
							<stop offset="1" stopColor="#0BFFE7" />
						</linearGradient>
					</defs>
				</svg>
			);
	}
}
